import { getTexts, type ITranslations } from '@/lib/i18n'
import type { IOrganizationCapabilities, ISectionCapability } from '@/types/capability'
import getSidebarSections from './getSidebarSections'

interface ICapabilityPage {
  key: string
  title: string
  url: string
  extraUrls: string[]
}

export interface ISidebarCapability {
  key: string
  title: string
  pages: ICapabilityPage[]
}

type ISidebarItem = Omit<ICapabilityPage, 'key' | 'extraUrls'> & { key?: string; extraUrls?: string[] }

const toPages = (pages: ISidebarItem[]) =>
  pages.flatMap(({ key, title, url, extraUrls = [] }) => (key ? [{ key, title, url, extraUrls }] : []))

// Sidebar sections gated by a capability (the ones with a key). A direct link is its own single page.
// Built without options, so per-organization sidebar variations (trust center, SIEM, hidden pages) don't change it
export default function getCapabilitySections(
  labels: ITranslations['SIDEBAR'] = getTexts().SIDEBAR,
): ISidebarCapability[] {
  return getSidebarSections(labels)
    .flatMap((group) => group.items)
    .flatMap(({ key, title, items, ...section }) =>
      key ? [{ key, title, pages: toPages(items ?? [{ key, title, ...section }]) }] : [],
    )
}

// The page itself, a page below it (detail pages) or one of its extra urls
export function isPathInPage(page: ICapabilityPage, pathname: string): boolean {
  return [page.url, ...page.extraUrls].some((url) => pathname === url || pathname.startsWith(`${url}/`))
}

// Keys of the sections a path belongs to
export function getPathSectionKeys(pathname: string): string[] {
  return getCapabilitySections()
    .filter(({ pages }) => pages.some((page) => isPathInPage(page, pathname)))
    .map(({ key }) => key)
}

export const isSectionOpen = (section?: ISectionCapability) => Object.values(section?.pages ?? {}).some(Boolean)

// A page not stored yet (a link added after the organization was saved) takes its section's state
const isPageOpen = (section: ISectionCapability | undefined, pageKey: string) =>
  section?.pages[pageKey] ?? isSectionOpen(section)

// The stored section with every one of its pages
export function resolveSection(
  { key, pages }: ISidebarCapability,
  capabilities: IOrganizationCapabilities,
): ISectionCapability {
  const section = capabilities[key]
  return {
    inScope: section?.inScope ?? false,
    pages: Object.fromEntries(pages.map((page) => [page.key, isPageOpen(section, page.key)])),
  }
}

export function hasPageAccess(capabilities: IOrganizationCapabilities, pathname: string): boolean {
  return getCapabilitySections().some(({ key, pages }) =>
    pages.some((page) => isPathInPage(page, pathname) && isPageOpen(capabilities[key], page.key)),
  )
}

// Only sidebar sections and their pages, with boolean values
export function isValidCapabilities(capabilities: IOrganizationCapabilities): boolean {
  const sections = getCapabilitySections()
  return Object.entries(capabilities).every(([key, value]) => {
    const section = sections.find((item) => item.key === key)
    const isValidPage = ([page, isOpen]: [string, unknown]) =>
      typeof isOpen === 'boolean' && Boolean(section?.pages.some((item) => item.key === page))
    return section && typeof value?.inScope === 'boolean' && Object.entries(value.pages ?? {}).every(isValidPage)
  })
}
